/* eslint-disable camelcase */
import dynamic from "next/dynamic";
import { useRouter } from "next/router";

import { GetStaticProps } from "next";
import { useQuery } from "@apollo/client";
import { useState, useCallback, useEffect } from "react";
import { ALL_POSTS } from "@graphql/allPosts";
import { initializeApollo } from "@lib/apolloClientConfig";
import styles from "@styles/pages/home.module.scss";

import SEO from "@components/SEO";
import Layout from "@components/Layout";

import useWindowSize from "../hooks/useWindowSize";

import { formatDateToPtBR } from "@utils/formatDate";
import { repoQuery, repoQueryVariables, repoQuery_posts } from "generated/repoQuery";
import { Filter } from "@components/Filter";

const Button = dynamic(() => import("@components/Button"));
const Banner = dynamic(() => import("@components/Banner"));
const PostHero = dynamic(() => import("@components/PostHero"));
const LastPosts = dynamic(() => import("@components/LastPosts"));
const FeaturedPosts = dynamic(() => import("@components/FeaturedPosts"));

export default function Home({
	firstPosts,
	featuredPosts,
	bannerPosts,
}: {
	firstPosts: repoQuery_posts;
	featuredPosts: repoQuery_posts;
	bannerPosts: repoQuery_posts;
}): JSX.Element {
	const router = useRouter();
	const [isLoading, setIsLoading] = useState(false);
	const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
	const { width: screenWidth } = useWindowSize();
	const pageUrl = `https://amigoedu.com.br${router.basePath}${router.asPath}`;
	const {
		data: { posts },
		error,
		loading,
		fetchMore,
		refetch,
	} = useQuery<repoQuery, repoQueryVariables>(ALL_POSTS, {
		variables: { first: 9, after: null },
	});

	const handleUpdateQuery = useCallback(async () => {
		const { endCursor, hasNextPage } = posts.pageInfo;

		const isMobile = screenWidth <= 425;

		if (hasNextPage) {
			setIsLoading(true);
			await fetchMore({
				variables: { after: endCursor, first: isMobile ? 6 : 9, categoryName: selectedCategory },
				updateQuery: (prevResult, { fetchMoreResult }) => {
					fetchMoreResult.posts.edges = [...prevResult.posts.edges, ...fetchMoreResult.posts.edges];
					return fetchMoreResult;
				},
			});

			setIsLoading(false);
		}
	}, [posts, screenWidth, fetchMore, selectedCategory]);

	if (loading) return <p>Loading...</p>;
	if (error) return <strong>Erro!</strong>;

	const bannerPost = bannerPosts?.edges[0]?.node ?? firstPosts.edges[0].node;

	return (
		<Layout>
			<>
				<SEO
					url={pageUrl}
					shouldIndexPage={true}
					shouldExcludeTitleSuffix
					title="Amigo Edu | Blog"
					description="Blog do Amigo Edu"
					uri={"/blogdoamigo"}
					canonicalPage={"https://amigoedu.com.br/blogdoamigo"}
				/>

				<section>
					<Banner
						srcSet={bannerPost.featuredImage?.node?.srcSet}
						altText={bannerPost.featuredImage?.node?.altText}
						coverImage={bannerPost.featuredImage?.node?.sourceUrl}
					>
						<PostHero
							title={bannerPost.title}
							date={formatDateToPtBR(bannerPost.date)}
							slug={bannerPost.slug}
							excerpt={bannerPost.excerpt}
						/>
					</Banner>

					{featuredPosts.edges.length > 0 && <FeaturedPosts posts={featuredPosts.edges.map((e) => e.node)} />}

					<Filter selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory} refetch={refetch} />

					{posts.edges.length > 0 ? (
						<LastPosts hideTitle posts={posts.edges.map((e) => e.node)} />
					) : (
						<section className={styles.emptyCategory}>
							<p>Nenhum post encontrado nessa categoria.</p>
						</section>
					)}
				</section>

				<footer className={styles.footer}>
					{posts.pageInfo.hasNextPage && (
						<Button outlined disabled={isLoading} loading={isLoading} onClick={handleUpdateQuery}>
							CARREGAR MAIS
						</Button>
					)}
				</footer>
			</>
		</Layout>
	);
}

export const getStaticProps: GetStaticProps = async () => {
	const apolloClient = initializeApollo();

	const { data } = await apolloClient.query<repoQuery, repoQueryVariables>({
		query: ALL_POSTS,
		variables: {
			first: 9,
			after: null,
		},
	});

	const { data: featuredData } = await apolloClient.query<repoQuery, repoQueryVariables>({
		query: ALL_POSTS,
		variables: {
			first: 3,
			after: null,
			tag: "destaque",
		},
		fetchPolicy: "no-cache",
	});

	const { data: bannerData } = await apolloClient.query<repoQuery, repoQueryVariables>({
		query: ALL_POSTS,
		variables: {
			first: 1,
			after: null,
			tag: "banner",
		},
		fetchPolicy: "no-cache",
	});

	return {
		props: {
			initialApolloState: apolloClient.cache.extract(),
			firstPosts: data.posts,
			featuredPosts: featuredData.posts,
			bannerPosts: bannerData.posts,
		},
		revalidate: 60,
	};
};
